import React from 'react';
import {Show, useRecordContext, useRedirect, useTranslate, usePermissions} from 'react-admin';
import {Box, Typography} from '@mui/material';
import {EditOutlined, DeleteOutline} from '@mui/icons-material';
import parse from 'html-react-parser';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import MailVariablesLegend from './MailVariablesLegend';
import MailTestPanel from './MailTestPanel';

const LANGUAGE_LABEL = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch',
    ru: 'Русский',
    hy: 'Հայերեն',
};

const PreviewSection = ({title, html}) => {
    if (!html) return null;
    return (
        <Box sx={{mb: 2}}>
            <Typography
                sx={{
                    fontSize: `${t.type.caption.size}px`,
                    fontWeight: 500,
                    color: t.color.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 0.5,
                }}
            >
                {title}
            </Typography>
            <Box
                sx={{
                    fontSize: `${t.type.body.size}px`,
                    lineHeight: 1.6,
                    color: t.color.text.primary,
                    '& p': {margin: 0, marginBottom: 1},
                    '& strong': {fontWeight: 600},
                }}
            >
                {parse(html)}
            </Box>
        </Box>
    );
};

const MailSettingsShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();

    if (!record) return null;

    const langLabel = LANGUAGE_LABEL[record.language_code] || String(record.language_code || '').toUpperCase();

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Mail settings'), to: '/mailSettings'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant="success"/>
                        <Box component="span">
                            {record.charity_name || `Umbrella #${record.charity_id}`}
                            <Box component="span" sx={{color: t.color.text.secondary, fontWeight: 400}}>
                                {' '}· {langLabel}
                            </Box>
                        </Box>
                    </Box>
                }
                actions={
                    <>
                        <PrimaryButton
                            variant="primary"
                            startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                            onClick={() => redirect('edit', 'mailSettings', record.id)}
                        >
                            {translate('Edit')}
                        </PrimaryButton>
                        {permissions === 'admin' && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard
                        title={translate('Registration mail')}
                        subtitle={translate('What new sponsors receive when they register')}
                    >
                        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
                            {record.charity_name && <StatusPill variant="neutral">{record.charity_name}</StatusPill>}
                            <StatusPill variant="info">{langLabel}</StatusPill>
                        </Box>
                        {record.logo_url && (
                            <Box sx={{mb: 2}}>
                                <Box
                                    component="img"
                                    src={record.logo_url}
                                    alt="Logo"
                                    sx={{maxHeight: 80, display: 'block'}}
                                />
                            </Box>
                        )}
                        <PreviewSection title={translate('Welcome text')} html={record.welcome_text}/>
                        <PreviewSection title={translate('App text before')} html={record.app_text_before}/>
                        <PreviewSection title={translate('Payment IBAN text')} html={record.payment_iban}/>
                        <PreviewSection title={translate('Payment other text')} html={record.payment_other}/>
                        <PreviewSection title={translate('Regards text')} html={record.regards_text}/>
                        <PreviewSection title={translate('Address text')} html={record.address_text}/>
                    </SectionCard>

                    {(record.update_subject || record.update_pre_text || record.update_post_text) && (
                        <SectionCard title={translate('Reminder for new posts')}>
                            {record.update_subject && (
                                <Box sx={{mb: 1.5}}>
                                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500, mb: 0.25}}>
                                        {translate('Subject')}
                                    </Typography>
                                    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
                                        {record.update_subject}
                                    </Typography>
                                </Box>
                            )}
                            <PreviewSection title={translate('Update pre text')} html={record.update_pre_text}/>
                            <PreviewSection title={translate('Update post text')} html={record.update_post_text}/>
                        </SectionCard>
                    )}

                    {(record.new_post_subject || record.pre_text || record.post_text) && (
                        <SectionCard title={translate('Daily new post mail')}>
                            {record.new_post_subject && (
                                <Box sx={{mb: 1.5}}>
                                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500, mb: 0.25}}>
                                        {translate('Subject')}
                                    </Typography>
                                    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
                                        {record.new_post_subject}
                                    </Typography>
                                </Box>
                            )}
                            <PreviewSection title={translate('New post pre text')} html={record.pre_text}/>
                            <PreviewSection title={translate('New post post text')} html={record.post_text}/>
                        </SectionCard>
                    )}
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <SectionCard title={translate('Send test mail')}>
                        <MailTestPanel/>
                    </SectionCard>
                    <SectionCard title={translate('Template variables')}>
                        <MailVariablesLegend/>
                    </SectionCard>
                </Box>
            </Box>

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="mailSettings"
                record={record}
                recordLabel={`${record.charity_name ?? 'Mail settings'} · ${langLabel}`}
            />
        </Box>
    );
};

const MailSettingsShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <MailSettingsShowContent/>
    </Show>
);

export default MailSettingsShowStatamic;
